/**
 * What a seller keeps from a price, and the line that explains the fee.
 *
 * Shared by the sell page's fee calculator and the "You Earn" row of the
 * owner's buy box, so the two quote the same number and the same words.
 */

import shared from '../data/js/shared';
import { calculateCommission, COMMISSION_RATE } from './commission';
import type { SellerFeeStatus } from '../schemas/api/seller/fee-status';

const { sellwildFeeTooltip, sellwildFeeTooltipRate, sellwildFeeTooltipReduced } = shared.post;

/** The fee on a price at a given rate, capped the way a real sale is. */
export const feeFor = (price: number, rate: number): number =>
  calculateCommission({ itemPrice: price, commissionRate: rate }).commissionAmount;

/**
 * The rate to charge. No status yet means no claim about the rate, so the base
 * rate stands in: it is the most anyone pays.
 */
export const sellerRate = (feeStatus: SellerFeeStatus | null | undefined): number =>
  feeStatus?.rate ?? COMMISSION_RATE;

/** "Sellwild fee is 10% of sold price", or the seller's own or reduced rate. */
export function sellwildFeeTooltipFor(feeStatus: SellerFeeStatus | null | undefined): string {
  if (!feeStatus) return sellwildFeeTooltip;
  if (feeStatus.isDiscounted) {
    return sellwildFeeTooltipReduced
      .replace('{rate}', feeStatus.rateLabel)
      .replace('{baseRate}', feeStatus.baseRateLabel);
  }
  return sellwildFeeTooltipRate.replace('{rate}', feeStatus.rateLabel);
}

/** The price less the Sellwild fee at this seller's rate. Zero for no price. */
export function sellerEarnings(price: number, feeStatus: SellerFeeStatus | null | undefined): number {
  if (!price || price <= 0) return 0;
  return price - feeFor(price, sellerRate(feeStatus));
}
